import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    DefaultButton,
    IconButton,
    IIconProps,
    Modal,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text
} from "@fluentui/react";

import {contentStyles, iconButtonStyles, nonShrinkingStackItemStyles} from "./Style"
import StatusMessage, {StatusEnum} from "../../share/StatusMessage";

import {FormProvider, useForm} from "react-hook-form";
import {useBranding} from '../../hooks/useBranding';
import ApprovalService, {
    IGetApprovalStepResponse,
    IPostApprovalStepRequest,
    IPutApprovalStepRequest
} from "../../services/Approval";
import {RHFTextBoxControl} from "../RHFControls/RHFTextBoxControl";
import {decode} from 'html-entities'

type Properties = {
    showModal: boolean
    onDismissModal: (update: boolean) => void;
    item: IGetApprovalStepResponse | null
    approvalId: number | null
    templateId: string
}

export function ApprovalStepEdit({
                                     approvalId,
                                     item,
                                     onDismissModal,
                                     showModal,
                                     templateId
                                 }: Properties) {

    const {t} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()
    const cancelIcon: IIconProps = {iconName: 'Cancel'};
    const form = useForm({
        defaultValues: {
            Name: item?.Name ?? '',
        }
    })

    const [isSaving, setIsSaving] = useState(false)
    const [message, setMessage] = useState("")
    const [showMessage, setShowMessage] = useState(false)
    const [statusMessageEnum, setStatusMessageEnum] = useState<StatusEnum>(StatusEnum.Info)


    const update = async () => {
        await form.handleSubmit(async () => {

                form.clearErrors()
                setIsSaving(true)

                const requestKIM: IPutApprovalStepRequest = {
                    ApprovalId: approvalId?.toString() ?? '',
                    Name: form.getValues("Name"),
                    TemplateId: templateId,
                    Order: item?.Order ?? 0,
                    Id: item?.Id.toString() ?? ''
                }

                await ApprovalService.putApprovalStep(requestKIM)
                    .then(data => {
                        if (data.Success) {
                            onDismissModal(true)
                        } else {
                            ShowStatusMessage(t('message.error', {
                                ns: 'common',
                                Error: data.ExceptionMessage
                            }), StatusEnum.Error)
                        }
                    })
                    .catch((error) => {
                        ShowStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
                    })
                    .finally(() => {
                        setIsSaving(false)
                    })

            }, () => {

            }
        )();
    }

    /**
     * Displays a status message with the specified content and status type.
     *
     * @function ShowStatusMessage
     * @param {string} message - The message text to be displayed.
     * @param {StatusEnum} status - An enumeration representing the type or status of the message (e.g., success, error).
     * @returns {void}
     */
    const ShowStatusMessage = (message: string, status: StatusEnum): void => {
        setMessage(decode(message))
        setStatusMessageEnum(status)
        setShowMessage(true)
    }

    /**
     * Asynchronous function to handle the creation of an approval step request.
     *
     * This function interacts with a form, retrieves form values, validates them, and submits validation errors if present.
     * Upon successful form submission, it sends a POST request to the ApprovalService to create an approval step.
     * Proper success or error messages are displayed to the user depending on the result of the request.
     *
     * The request data includes approval ID, name, and template ID.
     * The function also manages the saving state indicator and handles any errors that may occur during the request.
     *
     * Error messages and success statuses are displayed using localized strings from a translation function (`t`)
     * with appropriate namespaces (`ns`).
     *
     * @async
     * @function create
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    const create = async (): Promise<void> => {
        await form.handleSubmit(async () => {

                form.clearErrors()
                setIsSaving(true)

                const requestKIM: IPostApprovalStepRequest = {
                    ApprovalId: approvalId?.toString() ?? '',
                    Name: form.getValues("Name"),
                    TemplateId: templateId
                }

                await ApprovalService.postApprovalStep(requestKIM)
                    .then(data => {
                        if (data.Success) {
                            onDismissModal(true)
                        } else {
                            ShowStatusMessage(t('message.error', {
                                ns: 'common',
                                Error: data.ExceptionMessage
                            }), StatusEnum.Error)
                        }
                    })
                    .catch((error) => {
                        ShowStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
                    })
                    .finally(() => {
                        setIsSaving(false)
                    })

            }, () => {

            }
        )();
    }

    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerRecord}
            >
                {/*-- START HEADER --*/}
                <Stack horizontal className={contentStyles(branding.theme).header}>


                    <StackItem grow={1}
                               styles={{root: {textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden"}}}>
                        <Text variant={"xLarge"} id={'approvalStepEditTitle'}
                              title={item === null ? t('title.newApprovalStep', {ns: 'approval'}) : t('title.editApprovalStep', {ns: 'approval'})}>
                            {item === null ? t('title.newApprovalStep', {ns: 'approval'}) : t('title.editApprovalStep', {ns: 'approval'})}
                        </Text>
                    </StackItem>
                    <Stack horizontal styles={{root: {alignItems: "center"}}}>
                        <IconButton
                            styles={iconButtonStyles}
                            iconProps={cancelIcon}
                            ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                            onClick={() => {
                                onDismissModal(false)
                            }}
                            title={t('Common.Button.Close', {ns: 'common'})}
                        />
                    </Stack>
                </Stack>
                <Separator/>

                {/*-- END HEADER --*/}

                {/*-- START BODY --*/}
                <Stack horizontal tokens={{padding: 8}} style={{overflow: "hidden"}}>

                    {/*-- START CONTENT --*/}
                    <Stack grow={1} style={{overflow: "hidden", padding: "0px 8px 8px 8px"}}>
                        <StackItem>


                            {showMessage &&
                                <>
                                    <StackItem styles={{root: {marginBottom: 16}}}>

                                        <StatusMessage status={statusMessageEnum} hasTimer={false}
                                                       truncated={true}
                                                       isMultiline={true}
                                                       dismiss={true}
                                                       setShowMessage={setShowMessage}>
                                            <div>{message}</div>
                                        </StatusMessage>
                                    </StackItem>

                                </>
                            }
                        </StackItem>


                        <FormProvider {...form}>
                            <form onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}>
                                <Stack>
                                    <StackItem grow={1}>

                                        <RHFTextBoxControl
                                            id={"Name"}
                                            label={t('label.ctrlName', {ns: 'common'})}

                                            rules={{
                                                required: t('text.fieldRequired', {ns: 'common'}),
                                                validate: {
                                                    required:
                                                        (value) => {
                                                            return !!value.trim() || t('text.fieldRequired', {ns: 'common'})
                                                        }
                                                },
                                            }}

                                            placeholder={t('label.ctrlName', {ns: 'common'})}
                                            title={t('label.ctrlName', {ns: 'common'})}
                                            ariaLabel={t('label.ctrlName', {ns: 'common'})}
                                            maxLength={20}
                                            control={form.control}
                                            setValue={form.setValue}
                                            getValues={form.getValues}
                                            trigger={form.trigger}
                                            disabled={isSaving}



                                            required
                                        >
                                        </RHFTextBoxControl>


                                    </StackItem>

                                </Stack>

                            </form>
                        </FormProvider>


                        {/*-- START FOOTER --*/}
                        <Separator></Separator>
                        <Stack horizontal style={{overflow: "hidden"}} styles={nonShrinkingStackItemStyles}>
                            <StackItem grow={1}>&nbsp;</StackItem>

                            {isSaving &&
                                <>
                                    <StackItem>
                                        <Spinner size={SpinnerSize.medium} labelPosition="left"
                                                 label={t('text.spinnerSaving', {ns: 'common'})}/>
                                    </StackItem>
                                </>
                            }

                            <StackItem>


                                {item !== null &&
                                    <>
                                        <PrimaryButton disabled={isSaving}
                                                       style={{marginLeft: 8}}
                                                       onClick={update}
                                                       text={t('Common.Button.Update', {ns: 'common'})}
                                                       title={t('Common.Button.Update', {ns: 'common'})}/>
                                    </>
                                }
                                {item === null &&
                                    <PrimaryButton disabled={isSaving}
                                                   style={{marginLeft: 8}}
                                                   onClick={create}
                                                   text={t('Common.Button.Create', {ns: 'common'})}
                                                   title={t('Common.Button.Create', {ns: 'common'})}/>
                                }

                                <DefaultButton
                                    onClick={() => {
                                        onDismissModal(false)
                                    }}
                                    style={{marginLeft: 8}}
                                    text={t('Common.Button.Close', {ns: 'common'})}
                                    title={t('Common.Button.Close', {ns: 'common'})}/>

                            </StackItem>
                        </Stack>
                        {/*-- START FOOTER --*/}

                    </Stack>
                    {/*-- END CONTENT --*/}

                </Stack>
                {/*-- END BODY --*/}

            </Modal>


        </>
    )
}
